import React from "react";
import { ThinkingStatus } from "../types/drawerTypes";

interface ThinkingIndicatorProps {
  thinkingText: string;
  thinkingStatuses: ThinkingStatus[];
  isLoading: boolean;
  isThinkingExpanded: boolean;
  isThinkingVisible: boolean;
  onToggleExpanded: () => void;
  onToggleVisible: () => void;
}

const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({
  thinkingText,
  thinkingStatuses,
  isLoading,
  isThinkingExpanded,
  isThinkingVisible,
  onToggleExpanded,
  onToggleVisible,
}) => {
  if (!thinkingText && (!thinkingStatuses || thinkingStatuses.length === 0)) {
    return null;
  }

  if (!isThinkingVisible && !isLoading) {
    return (
      <div className="flex justify-start">
        <button
          onClick={onToggleVisible}
          className="text-xs px-3 py-1 rounded hover:underline"
          style={{
            color: "rgba(15, 53, 87, 0.7)",
            backgroundColor: "rgba(15, 53, 87, 0.05)",
          }}
        >
          Show thinking process
        </button>
      </div>
    );
  }

  if (!isThinkingVisible) {
    return null;
  }

  return (
    <div className="flex justify-start">
      <div
        className="max-w-[85%] rounded-lg p-3 shadow-sm"
        style={{
          backgroundColor: "rgba(15, 53, 87, 0.08)",
          border: "1px solid rgba(15, 53, 87, 0.2)",
        }}
      >
        <div className="flex items-start space-x-2">
          {isLoading && (
            <div
              className="animate-spin rounded-full h-4 w-4 border-b-2 mt-0.5 flex-shrink-0"
              style={{ borderColor: "#0f3557" }}
            ></div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <div
                className="text-xs font-medium"
                style={{ color: "#0f3557" }}
              >
                Thinking...
              </div>
              {!isLoading && (
                <button
                  onClick={onToggleVisible}
                  className="text-xs ml-2 hover:underline"
                  style={{ color: "rgba(15, 53, 87, 0.7)" }}
                >
                  Hide
                </button>
              )}
            </div>
            {thinkingText && (
              <div className="mb-2">
                <div
                  className={`text-sm whitespace-pre-wrap break-words overflow-hidden ${!isThinkingExpanded ? "line-clamp-4" : ""
                    }`}
                  style={{ color: "rgba(15, 53, 87, 0.9)" }}
                >
                  {thinkingText}
                </div>
                {(thinkingText.length > 200 ||
                  thinkingText.split("\n").length > 4) && (
                    <button
                      onClick={onToggleExpanded}
                      className="text-xs mt-1 hover:underline"
                      style={{ color: "rgba(15, 53, 87, 0.7)" }}
                    >
                      {isThinkingExpanded ? "Show less" : "Show more"}
                    </button>
                  )}
              </div>
            )}
            {thinkingStatuses.length > 0 && (
              <div className="space-y-1">
                {thinkingStatuses.length > 5 && (
                  <div
                    className="text-xs text-center py-1"
                    style={{ color: "rgba(15, 53, 87, 0.6)" }}
                  >
                    ...
                  </div>
                )}
                {thinkingStatuses.slice(-5).map((statusEntry, idx) => (
                  <div
                    key={`${statusEntry.status}-${idx}`}
                    className="text-xs break-words"
                    style={{ color: "rgba(15, 53, 87, 0.85)" }}
                  >
                    <span className="font-semibold">
                      {statusEntry.status}:
                    </span>{" "}
                    {statusEntry.message}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThinkingIndicator;
